import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Ghost, Send, MessageCircle, X, Minus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ChatMessage {
  id: string;
  pubkey: string;
  content: string;
  created_at: number;
  authorName?: string;
  authorPicture?: string;
}

export function CemeteryChat() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch chat messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const signal = AbortSignal.any([AbortSignal.timeout(10000)]);
        const events = await nostr.query([
          {
            kinds: [42], // NIP-28 public chat messages
            '#t': ['blockcemetery'],
            limit: 50,
          }
        ], { signal });

        const chatMessages: ChatMessage[] = events.map(event => ({
          id: event.id,
          pubkey: event.pubkey,
          content: event.content,
          created_at: event.created_at,
        }));

        setMessages(chatMessages.sort((a, b) => a.created_at - b.created_at));
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch chat messages:', error);
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [nostr]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    try {
      const event = await publish({
        kind: 42,
        content: newMessage.trim(),
        tags: [
          ['t', 'blockcemetery'],
          ['alt', 'Chat message in Block Cemetery'],
        ],
      });

      // Add message to local state
      setMessages(prev => [...prev, {
        id: event.id,
        pubkey: user.pubkey,
        content: newMessage.trim(),
        created_at: event.created_at,
        authorName: user.metadata?.name,
        authorPicture: user.metadata?.picture,
      }]);

      setNewMessage('');
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsMinimized(false)}
          size="lg"
          className="bg-primary/90 backdrop-blur-sm border-primary/30 shadow-lg"
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          Cemetery Chat
        </Button>
      </div>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-full max-w-md z-50 bg-card/95 backdrop-blur-md border-primary/30 shadow-2xl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-gothic text-primary">
            <Ghost className="h-5 w-5 animate-pulse" />
            Cemetery Chat 👻
          </CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(true)}
              className="h-8 w-8"
            >
              {isMobile ? <X className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <ScrollArea className="h-64 w-full rounded-md border border-border/50 p-3">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Ghost className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">The cemetery is quiet...</p>
              <p className="text-xs">Be the first to speak! 👻</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <ChatMessageItem key={message.id} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
        
        {user ? (
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a spooky message..."
              className="flex-1"
              disabled={!user}
            />
            <Button
              onClick={sendMessage}
              disabled={!newMessage.trim() || !user}
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">
            Login to join the chat
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface ChatMessageItemProps {
  message: ChatMessage;
}

function ChatMessageItem({ message }: ChatMessageItemProps) {
  const { data: author } = useAuthor(message.pubkey);
  const { user } = useCurrentUser();
  const isCurrentUser = message.pubkey === user?.pubkey;
  
  const authorName = author?.metadata?.name || message.authorName || 'Anonymous Ghost';
  const authorPicture = author?.metadata?.picture || message.authorPicture;
  const timeAgo = formatDistanceToNow(new Date(message.created_at * 1000), { addSuffix: true });

  return (
    <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className={isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}>
          {authorName.charAt(0).toUpperCase()}
        </AvatarFallback>
        {authorPicture && (
          <img src={authorPicture} alt={authorName} className="h-full w-full object-cover" />
        )}
      </Avatar>
      
      <div className={`flex-1 space-y-1 ${isCurrentUser ? 'text-right' : ''}`}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">{authorName}</span>
          <span>•</span>
          <span>{timeAgo}</span>
        </div>
        
        <div className={`inline-block rounded-lg px-3 py-2 max-w-[80%] break-words ${
          isCurrentUser 
            ? 'bg-primary text-primary-foreground' 
            : 'bg-muted text-muted-foreground'
        }`}>
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    </div>
  );
}
